import { useState } from "react";
import axios from "axios";
import { pushEvent, currentPagePath } from "../lib/analytics";
import {
  AirplaneTilt, CheckCircle, Circle, DotsThreeCircle, ShieldCheck, Package,
  MapPin, MagnifyingGlass,
} from "@phosphor-icons/react";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "../components/ui/carousel";
import { fmtDT } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_ICON = {
  completed: <CheckCircle size={20} weight="fill" className="text-[hsl(var(--success,142_70%_45%))]" />,
  current: <DotsThreeCircle size={20} weight="fill" className="text-[hsl(var(--accent,221_83%_53%))]" />,
  pending: <Circle size={20} className="text-[hsl(var(--muted))]" />,
};

// Milestone instants render in Asia/Kolkata (IST) via the shared fmtDT formatter so the shown time
// is identical regardless of the viewer's browser timezone and carries an explicit IST suffix.
const formatTs = (iso) => (iso ? fmtDT(iso) : "");

// Founder-locked visual evidence: view-only stamped photos beneath a reached milestone.
// The image itself carries burned-in GPS/time; no extra caption, no download/share controls.
function MilestonePhotos({ photos, milestoneKey }) {
  if (!photos || photos.length === 0) return null;
  const single = photos.length === 1;
  const Img = ({ p }) => (
    <img
      src={p.url}
      alt=""
      loading="lazy"
      className="w-full h-44 object-contain rounded-md border border-[hsl(var(--border,220_13%_88%))] bg-[hsl(var(--muted))/0.15]"
    />
  );

  if (single) {
    return (
      <div className="mt-2" data-testid={`track-photos-${milestoneKey}`} data-photo-count={photos.length}>
        <Img p={photos[0]} />
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid={`track-photos-${milestoneKey}`} data-photo-count={photos.length}>
      <Carousel opts={{ align: "start" }} className="w-full">
        <CarouselContent>
          {photos.map((p, i) => (
            <CarouselItem key={p.id || i} className="basis-4/5 sm:basis-2/3">
              <Img p={p} />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious type="button" className="left-1 h-7 w-7" data-testid={`track-photos-${milestoneKey}-prev`} />
        <CarouselNext type="button" className="right-1 h-7 w-7" data-testid={`track-photos-${milestoneKey}-next`} />
      </Carousel>
    </div>
  );
}

export default function TrackShipment() {
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [otp, setOtp] = useState("");
  const [grBusy, setGrBusy] = useState(false);
  const [grError, setGrError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setData(null); setGrError(null); setOtp("");
    try {
      const r = await axios.post(`${API}/public/track`, {
        shipment_code: code.trim(),
        tracking_password: password.trim(),
      });
      setData(r.data);
      pushEvent("track_shipment_search", { page_path: currentPagePath(), form_name: "track_shipment", search_result: "found" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid shipment code or tracking password.");
      pushEvent("track_shipment_search", { page_path: currentPagePath(), form_name: "track_shipment", search_result: err?.response ? "not_found" : "error" });
    } finally {
      setBusy(false);
    }
  };

  const confirmGoodsReceived = async () => {
    setGrBusy(true); setGrError(null);
    try {
      const r = await axios.post(`${API}/public/track/goods-received`, {
        shipment_code: code.trim(),
        tracking_password: password.trim(),
        otp: otp.trim(),
      });
      setData(r.data);
      setOtp("");
    } catch (err) {
      // Backend returns a generic message for the public path; never reveals internal Block state.
      const detail = err?.response?.data?.detail;
      setGrError(typeof detail === "string" ? detail : "We couldn't confirm receipt right now. Please try again or contact Air12 Support.");
    } finally {
      setGrBusy(false);
    }
  };

const isOutForDelivery = data && data.public_status === "Out for delivery";


  return (
    <div className="min-h-screen bg-[hsl(var(--bg,0_0%_98%))] flex flex-col items-center px-4 py-10">
      {/* Brand header */}
      <div className="w-full max-w-xl flex items-center gap-3 mb-8" data-testid="track-header">
        <div className="w-10 h-10 rounded-lg bg-[hsl(var(--accent,221_83%_53%))] flex items-center justify-center">
          <AirplaneTilt size={22} weight="fill" className="text-white" />
        </div>
        <div>
          <div className="heading text-lg leading-tight">Air12</div>
          <div className="overline text-[hsl(var(--muted))]">SHIPMENT TRACKING</div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="grid-card grid-card-strong p-6 w-full max-w-xl space-y-4" data-testid="track-form">
        <h1 className="heading text-xl">Track your shipment</h1>
        <p className="text-sm text-[hsl(var(--muted))]">
          Enter your Air12 shipment code and the tracking password from your booking email.
        </p>

        <div className="space-y-1">
          <label className="overline" htmlFor="track-code">AIR12 SHIPMENT CODE</label>
          <input
            id="track-code"
            data-testid="track-shipment-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="A12-BOMDEL-YYYYMMDD-NNNN"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--border,220_13%_88%))] bg-white text-sm mono focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent,221_83%_53%))]"
            autoComplete="off"
            required
          />
        </div>

        <div className="space-y-1">
          <label className="overline" htmlFor="track-pw">TRACKING PASSWORD</label>
          <input
            id="track-pw"
            data-testid="track-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="8-character code"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--border,220_13%_88%))] bg-white text-sm mono uppercase focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent,221_83%_53%))]"
            autoComplete="off"
            required
          />
        </div>

        <button
          type="submit"
          data-testid="track-submit-button"
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[hsl(var(--accent,221_83%_53%))] text-white text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <MagnifyingGlass size={16} weight="bold" />
          {busy ? "Tracking…" : "Track Shipment"}
        </button>

        {error && (
          <div data-testid="track-error" className="text-sm text-[hsl(var(--error))] border border-[hsl(var(--error))] rounded-md px-3 py-2">
            {error}
          </div>
        )}
      </form>

      {/* Result */}
      {data && (
        <div className="grid-card grid-card-strong p-6 w-full max-w-xl mt-6 space-y-5" data-testid="track-result">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="overline text-[hsl(var(--muted))]">SHIPMENT CODE</div>
              <div className="mono text-base" data-testid="track-result-code">{data.shipment_code}</div>
            </div>
            <div className="px-3 py-1.5 rounded-full bg-[hsl(var(--accent,221_83%_53%))/0.1] text-[hsl(var(--accent,221_83%_53%))] text-sm font-medium" data-testid="track-result-status">
              {data.public_status}
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm" data-testid="track-result-route">
            <MapPin size={16} className="text-[hsl(var(--muted))]" />
            <span className="font-medium">{data.origin}</span>
            <AirplaneTilt size={14} className="text-[hsl(var(--muted))]" />
            <span className="font-medium">{data.destination}</span>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div className="flex items-center gap-2" data-testid="track-result-packages">
              <Package size={16} className="text-[hsl(var(--muted))]" />
              <span>{data.package_count} package{data.package_count === 1 ? "" : "s"}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="track-result-insurance">
              <ShieldCheck size={16} className="text-[hsl(var(--muted))]" />
              <span>{data.insurance_status}</span>
            </div>
          </div>

          {/* Chain-of-custody strip */}
          <div className="pt-2" data-testid="track-timeline">
            <div className="overline text-[hsl(var(--muted))] mb-3">CHAIN OF CUSTODY</div>
            <ol className="relative">
              {data.timeline.map((t, i) => {
                const last = i === data.timeline.length - 1;
                const connectorColor =
                  t.state === "completed"
                    ? "bg-[hsl(var(--success,142_70%_45%))]"
                    : "bg-[hsl(var(--border,220_13%_88%))]";
                return (
                  <li
                    key={t.key || t.milestone}
                    className="relative flex gap-3 pb-5 last:pb-0"
                    data-testid={`track-milestone-${t.state}`}
                    data-milestone-key={t.key}
                  >
                    {/* connector rail */}
                    {!last && (
                      <span className={`absolute left-[9px] top-6 bottom-0 w-0.5 ${connectorColor}`} aria-hidden="true" />
                    )}
                    <span className="relative z-10 shrink-0 mt-0.5">
                      {STATE_ICON[t.state] || STATE_ICON.pending}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div
                        className={
                          t.state === "current"
                            ? "text-sm font-semibold break-words"
                            : t.state === "completed"
                              ? "text-sm break-words"
                              : "text-sm text-[hsl(var(--muted))] break-words"
                        }
                      >
                        {t.label || t.milestone}
                      </div>
                      {t.timestamp && (
                        <div className="mono text-xs text-[hsl(var(--muted))] mt-0.5" data-testid={`track-milestone-time-${t.key}`}>
                          {formatTs(t.timestamp)}
                        </div>
                      )}
                      <MilestonePhotos photos={t.photos} milestoneKey={t.key} />
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* M9B U6 — Goods Received (public confirmation) at Out for delivery */}
          {isOutForDelivery && (
            <div className="pt-2 border-t border-[hsl(var(--border,220_13%_88%))]" data-testid="goods-received-section">
              <div className="overline text-[hsl(var(--muted))] mb-2">CONFIRM GOODS RECEIVED</div>
              <p className="text-sm text-[hsl(var(--muted))] mb-3">
                When your shipment has reached you, enter the delivery code sent to your email to
                confirm receipt.
              </p>
              <input
                data-testid="public-goods-received-otp-input"
                value={otp}
                inputMode="numeric"
                maxLength={6}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit delivery code"
                className="w-full px-3 py-2 rounded-md border border-[hsl(var(--border,220_13%_88%))] bg-white text-sm mono tracking-widest focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent,221_83%_53%))]"
                autoComplete="off"
              />
              <button
                data-testid="public-goods-received-submit"
                disabled={grBusy || otp.length < 4}
                onClick={confirmGoodsReceived}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[hsl(var(--success,142_70%_45%))] text-white text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                <CheckCircle size={16} weight="bold" />
                {grBusy ? "Confirming…" : "Confirm Goods Received"}
              </button>
              {grError && (
                <div data-testid="goods-received-error" className="mt-3 text-sm text-[hsl(var(--error))] border border-[hsl(var(--error))] rounded-md px-3 py-2">
                  {grError}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-[hsl(var(--muted))] mt-8 max-w-xl text-center">
        For any assistance with your shipment, please contact Air12 Support.
      </p>
    </div>
  );
}
